import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  MdAdd,
  MdClose,
  MdDelete,
  MdDirectionsBoat,
  MdEdit,
  MdFileUpload,
  MdSearch,
  MdCloudDownload,
  MdSatelliteAlt,
} from 'react-icons/md';
import { authenticatedFetch } from '../../lib/api';
import { downloadFromResponse } from '../../utils/download';
import { useToast } from '../../components/shared/Toast';
import { Pagination } from '../../components/shared/Pagination';
import { useAuth } from '../../context/AuthContext';
import { getAdminVesselAccess } from '../../utils/vesselPermissions';
import { useDebouncedSearch } from '@euroasiann/shared';
import {
  ClearFiltersButton,
  erpPageBreadcrumbClass,
  erpPageBreadcrumbCurrentClass,
  erpPageBreadcrumbItemClass,
  erpPageBreadcrumbSepClass,
  TableBodySkeleton,
} from '@euroasiann/shared/ui';

const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? '' : 'http://localhost:3000');

type VesselStatus =
  | 'active'
  | 'inactive'
  | 'commissioned'
  | 'decommissioned'
  | 'maintenance'
  | 'laid-up'
  | 'sunk'
  | 'unknown';

type VesselSource = 'manual' | 'portal' | 'ais' | 'external' | 'vesselapi';

interface GlobalVessel {
  _id: string;
  name?: string;
  imoNumber?: string;
  mmsi?: string;
  callSign?: string;
  flag?: string;
  vesselType?: string;
  status: VesselStatus;
  source?: VesselSource;
  createdAt?: string;
  updatedAt?: string;
}

interface VesselFormData {
  name: string;
  imoNumber: string;
  mmsi: string;
  callSign: string;
  flag: string;
  vesselType: string;
  status: VesselStatus;
}

const INITIAL_FORM: VesselFormData = {
  name: '',
  imoNumber: '',
  mmsi: '',
  callSign: '',
  flag: '',
  vesselType: '',
  status: 'active',
};

function displayValue(value?: string) {
  return value?.trim() ? value : '—';
}

function hasAnyIdentifier(data: VesselFormData) {
  return [data.name, data.imoNumber, data.mmsi, data.callSign].some((v) => v.trim());
}

function sourceBadgeClass(source?: VesselSource): string {
  if (source === 'portal') return 'bg-violet-100 text-violet-700';
  if (source === 'ais') return 'bg-sky-100 text-sky-700';
  if (source === 'vesselapi') return 'bg-emerald-100 text-emerald-800';
  if (source === 'external') return 'bg-amber-100 text-amber-800';
  return 'bg-[#0088cc]/10 text-[hsl(var(--muted-foreground))]';
}

export function VesselDatabasePage() {
  const { showToast } = useToast();
  const { user, permissions } = useAuth();
  const { canView, canCreate, canEdit, canDelete } = getAdminVesselAccess(user?.role, permissions);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [vessels, setVessels] = useState<GlobalVessel[]>([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [fetchingGlobal, setFetchingGlobal] = useState(false);
  const [fetchProgress, setFetchProgress] = useState(0);
  const [fetchJobId, setFetchJobId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const {
    inputValue: searchQuery,
    submittedValue: submittedSearch,
    setInputValue: setSearchQuery,
    clear: clearSearch,
    onKeyDown: onSearchKeyDown,
  } = useDebouncedSearch('', {
    onCommit: () => setPage(1),
  });
  const [statusFilter, setStatusFilter] = useState<VesselStatus | 'all'>('active');
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingVessel, setEditingVessel] = useState<GlobalVessel | null>(null);
  const [formData, setFormData] = useState<VesselFormData>(INITIAL_FORM);
  const [enrichingId, setEnrichingId] = useState<string | null>(null);

  const fetchAbortRef = useRef<AbortController | null>(null);

  const fetchVessels = async () => {
    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (submittedSearch) params.append('search', submittedSearch);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('page', String(page));
      params.append('limit', String(limit));

      const response = await authenticatedFetch(`/api/v1/vessel-database?${params.toString()}`, {
        signal: controller.signal,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to fetch vessels');
      }

      const payload = await response.json();
      if (controller.signal.aborted) return;
      setVessels(Array.isArray(payload.data) ? payload.data : []);
      setTotal(payload.meta?.total ?? 0);
    } catch (error: any) {
      if (error?.name === 'AbortError') return;
      showToast(error.message || 'Failed to fetch vessels', 'error');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    if (!canView) return;
    void fetchVessels();
  }, [submittedSearch, statusFilter, page, canView]);

  useEffect(() => () => fetchAbortRef.current?.abort(), []);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  const openCreateModal = () => {
    if (!canCreate) return;
    setEditingVessel(null);
    setFormData(INITIAL_FORM);
    setIsModalOpen(true);
  };

  const openEditModal = (vessel: GlobalVessel) => {
    if (!canEdit) return;
    setEditingVessel(vessel);
    setFormData({
      name: vessel.name || '',
      imoNumber: vessel.imoNumber || '',
      mmsi: vessel.mmsi || '',
      callSign: vessel.callSign || '',
      flag: vessel.flag || '',
      vesselType: vessel.vesselType || '',
      status: vessel.status,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingVessel(null);
    setFormData(INITIAL_FORM);
  };

  const handleSubmit = async () => {
    if (editingVessel ? !canEdit : !canCreate) {
      showToast('You do not have permission to save vessels', 'error');
      return;
    }
    if (!hasAnyIdentifier(formData)) {
      showToast('Provide at least one of vessel name, IMO, MMSI, or call sign', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const endpoint = editingVessel
        ? `/api/v1/vessel-database/${editingVessel._id}`
        : '/api/v1/vessel-database';

      const response = await authenticatedFetch(endpoint, {
        method: editingVessel ? 'PUT' : 'POST',
        body: JSON.stringify({
          name: formData.name.trim() || undefined,
          imoNumber: formData.imoNumber.trim() || undefined,
          mmsi: formData.mmsi.trim() || undefined,
          callSign: formData.callSign.trim() || undefined,
          flag: formData.flag.trim() || undefined,
          vesselType: formData.vesselType.trim() || undefined,
          status: formData.status,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to save vessel');
      }

      showToast(
        editingVessel ? 'Vessel updated successfully' : 'Vessel added to global database',
        'success'
      );
      closeModal();
      await fetchVessels();
    } catch (error: any) {
      showToast(error.message || 'Failed to save vessel', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (vessel: GlobalVessel) => {
    if (!canDelete) return;
    const label = vessel.name || vessel.imoNumber || vessel.mmsi || vessel.callSign || 'this vessel';
    if (!window.confirm(`Remove "${label}" from the global vessel database?`)) return;

    try {
      const response = await authenticatedFetch(`/api/v1/vessel-database/${vessel._id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to delete vessel');
      }
      showToast('Vessel removed from global database', 'success');
      await fetchVessels();
    } catch (error: any) {
      showToast(error.message || 'Failed to delete vessel', 'error');
    }
  };

  const downloadFile = async (path: string, filename: string) => {
    const token = localStorage.getItem('accessToken');
    const url = API_URL ? `${API_URL}${path}` : path;
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Download failed');
    await downloadFromResponse(response, filename, '.xlsx');
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (submittedSearch) params.append('search', submittedSearch);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const qs = params.toString();
      await downloadFile(
        `/api/v1/vessel-database/export${qs ? `?${qs}` : ''}`,
        `vessel-database-${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      showToast('Vessel database exported to Excel', 'success');
    } catch (error: any) {
      showToast(error.message || 'Failed to export vessels', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadFile('/api/v1/vessel-database/template', 'vessel-database-template.xlsx');
      showToast('Template downloaded', 'success');
    } catch (error: any) {
      showToast(error.message || 'Failed to download template', 'error');
    }
  };

  const handleAisEnrich = async (vessel: GlobalVessel) => {
    if (!canEdit) return;
    if (!vessel.mmsi) {
      showToast('Add a 9-digit MMSI to this vessel first to look up AIS data', 'error');
      return;
    }
    setEnrichingId(vessel._id);
    try {
      const response = await authenticatedFetch(`/api/v1/vessel-database/${vessel._id}/ais-enrich`, {
        method: 'POST',
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'AIS lookup failed');
      }
      showToast(payload.message || 'Vessel enriched with AIS data', payload.updated ? 'success' : 'info');
      if (payload.updated) await fetchVessels();
    } catch (error: any) {
      showToast(error.message || 'AIS lookup failed', 'error');
    } finally {
      setEnrichingId(null);
    }
  };

  const pollFetchJob = async (jobId: string) => {
    const maxAttempts = 3600;
    for (let i = 0; i < maxAttempts; i += 1) {
      await new Promise((r) => setTimeout(r, 2000));
      const statusRes = await authenticatedFetch(`/api/v1/vessel-database/fetch-global/${jobId}/status`);
      if (!statusRes.ok) continue;
      const statusJson = await statusRes.json();
      const progress = typeof statusJson.progress === 'number' ? statusJson.progress : 0;
      setFetchProgress(progress);

      if (statusJson.state === 'completed') {
        showToast('Global vessel fetch completed', 'success');
        setFetchJobId(null);
        setFetchProgress(100);
        setPage(1);
        await fetchVessels();
        return;
      }
      if (statusJson.state === 'failed') {
        throw new Error(statusJson.failedReason || 'Global fetch job failed');
      }
    }
    throw new Error('Global fetch timed out — check API logs');
  };

  const handleFetchGlobalVessels = async () => {
    if (!canCreate) return;
    setFetchingGlobal(true);
    setFetchProgress(0);
    setFetchJobId(null);
    try {
      const response = await authenticatedFetch('/api/v1/vessel-database/fetch-global', {
        method: 'POST',
      });
      const payload = await response.json().catch(() => ({}));

      if (response.status === 202 && payload.jobId) {
        setFetchJobId(String(payload.jobId));
        showToast('Importing missing vessels from global APIs…', 'info');
        await pollFetchJob(String(payload.jobId));
        return;
      }

      if (!response.ok) {
        throw new Error(payload.error || 'Failed to fetch global vessels');
      }
      showToast(payload.message || 'Global vessels fetched successfully', 'success');
      setFetchProgress(100);
      setPage(1);
      await fetchVessels();
    } catch (error: any) {
      showToast(error.message || 'Failed to fetch global vessels', 'error');
    } finally {
      setFetchingGlobal(false);
      setTimeout(() => setFetchProgress(0), 1500);
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canCreate) return;
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setImporting(true);
    try {
      const form = new FormData();
      form.append('file', file);

      const token = localStorage.getItem('accessToken');
      const url = API_URL ? `${API_URL}/api/v1/vessel-database/import` : '/api/v1/vessel-database/import';
      const response = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Import failed');
      }

      const stats = payload.data;
      showToast(
        payload.message ||
          `Import complete: ${stats?.created ?? 0} created, ${stats?.updated ?? 0} updated`,
        stats?.failed ? 'warning' : 'success'
      );

      if (stats?.errors?.length) {
        console.warn('Vessel import errors:', stats.errors);
      }

      setPage(1);
      await fetchVessels();
    } catch (error: any) {
      showToast(error.message || 'Failed to import vessels', 'error');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="w-full min-h-screen p-8">
      <div className="mb-8 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        <div>
          <nav className={`${erpPageBreadcrumbClass} mb-2`} aria-label="Breadcrumb">
            <span className={erpPageBreadcrumbItemClass}>Admin</span>
            <span className={erpPageBreadcrumbSepClass}>/</span>
            <span className={erpPageBreadcrumbCurrentClass}>Vessel Database</span>
          </nav>
          <h1 className="text-3xl font-bold text-[hsl(var(--foreground))] flex items-center gap-3">
            <MdDirectionsBoat className="text-[#0088cc]" />
            Vessel Database
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-2xl">
            Master vessel directory for <strong>all portals</strong> (customer, vendor, RFQ, fleet tracking).
            Any vessel saved in a portal is added here <strong>automatically</strong>.
            Use <strong>Fetch Global Vessels</strong> only to import ships that are not in the list yet (AIS cache, VesselAPI, Datalastic).
          </p>
        </div>

        {canView && (
        <div className="flex flex-wrap gap-2">
          {canCreate && (
            <button
              onClick={handleDownloadTemplate}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-[hsl(var(--border))] rounded-lg hover:bg-[hsl(var(--muted))]"
            >
              <MdCloudDownload className="w-4 h-4" />
              Template
            </button>
          )}
          {canCreate && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-[hsl(var(--border))] rounded-lg hover:bg-[hsl(var(--muted))] disabled:opacity-60"
            >
              <MdFileUpload className="w-4 h-4" />
              {importing ? 'Importing...' : 'Import Excel'}
            </button>
          )}
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-[hsl(var(--border))] rounded-lg hover:bg-[hsl(var(--muted))] disabled:opacity-60"
          >
            <MdCloudDownload className="w-4 h-4" />
            {exporting ? 'Exporting...' : 'Export Excel'}
          </button>
          {canCreate && (
            <button
              onClick={handleFetchGlobalVessels}
              disabled={fetchingGlobal}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-[hsl(var(--border))] rounded-lg hover:bg-[hsl(var(--muted))] disabled:opacity-60"
            >
              <MdCloudDownload className="w-4 h-4" />
              {fetchingGlobal ? 'Importing...' : 'Import Missing Vessels'}
            </button>
          )}
          {canCreate && (
            <button
              onClick={openCreateModal}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0088cc] text-white rounded-lg hover:bg-[#0077b3]"
            >
              <MdAdd className="w-4 h-4" />
              Add Vessel
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={handleImport}
          />
        </div>
        )}
      </div>

      {!canView ? (
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-6 py-16 text-center">
          <p className="text-lg font-semibold text-[hsl(var(--foreground))]">Read access required</p>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
            You need the View Vessel permission to open the vessel database.
          </p>
        </div>
      ) : (
      <>

      {(fetchingGlobal || fetchProgress > 0) && (
        <div className="mb-4 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 p-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="font-medium">
              {fetchJobId ? `Fetching global vessels (job ${fetchJobId})…` : 'Fetching global vessels…'}
            </span>
            <span>{fetchProgress}%</span>
          </div>
          <div className="h-2 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
            <div
              className="h-full bg-[#0088cc] transition-all duration-500"
              style={{ width: `${Math.min(fetchProgress, 100)}%` }}
            />
          </div>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">
            Imports missing vessels from AIS → VesselAPI → Datalastic. Portal saves already appear here automatically.
            Requires <code className="text-xs">VESSELAPI_API_KEY</code> for worldwide vessel import.
          </p>
        </div>
      )}

      <div className="mb-6 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
        <div className="flex flex-wrap gap-2 border-b border-[hsl(var(--border))]">
          {(
            [
              'active',
              'inactive',
              'commissioned',
              'decommissioned',
              'maintenance',
              'laid-up',
              'sunk',
              'unknown',
              'all',
            ] as const
          ).map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setStatusFilter(tab);
                setPage(1);
              }}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors capitalize ${
                statusFilter === tab
                  ? 'border-[#0088cc] text-[hsl(var(--primary))]'
                  : 'border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="relative w-full md:w-80 flex items-center gap-2">
          <div className="relative flex-1">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
            <input
              type="text"
              placeholder="Search name, IMO, MMSI, call sign..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={onSearchKeyDown}
              className="w-full pl-10 pr-4 py-2 text-sm border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))]"
            />
          </div>
          <ClearFiltersButton
            visible={!!searchQuery.trim() || statusFilter !== 'active'}
            onClick={() => {
              clearSearch();
              setStatusFilter('active');
              setPage(1);
            }}
            variant="toolbar"
          />
        </div>
      </div>

      <div className="rounded-xl border border-[hsl(var(--border))] overflow-hidden bg-[hsl(var(--card))]">
        <div className="overflow-x-auto">
          <table className="w-full text-base">
            <thead className="bg-[hsl(var(--muted))]/50">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Vessel Name</th>
                <th className="text-left px-4 py-3 font-semibold">IMO Number</th>
                <th className="text-left px-4 py-3 font-semibold">MMSI</th>
                <th className="text-left px-4 py-3 font-semibold">Call Sign</th>
                <th className="text-left px-4 py-3 font-semibold">Flag State</th>
                <th className="text-left px-4 py-3 font-semibold">Type of Vessel</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Source</th>
                {(canEdit || canDelete) && (
                  <th className="text-right px-4 py-3 font-semibold">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableBodySkeleton rows={6} cols={canEdit || canDelete ? 9 : 8} />
              ) : vessels.length === 0 ? (
                <tr>
                  <td colSpan={canEdit || canDelete ? 9 : 8} className="px-4 py-10 text-center text-[hsl(var(--muted-foreground))]">
                    No vessels found{canCreate ? '. Add manually or import from Excel.' : '.'}
                  </td>
                </tr>
              ) : (
                vessels.map((vessel) => (
                  <tr key={vessel._id} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/30">
                    <td className="px-4 py-3 font-medium">{displayValue(vessel.name)}</td>
                    <td className="px-4 py-3 font-mono">{displayValue(vessel.imoNumber)}</td>
                    <td className="px-4 py-3 font-mono">{displayValue(vessel.mmsi)}</td>
                    <td className="px-4 py-3">{displayValue(vessel.callSign)}</td>
                    <td className="px-4 py-3">{displayValue(vessel.flag)}</td>
                    <td className="px-4 py-3">{displayValue(vessel.vesselType)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                          vessel.status === 'active' || vessel.status === 'commissioned'
                            ? 'bg-green-100 text-green-700'
                            : vessel.status === 'sunk' || vessel.status === 'decommissioned'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-[#0088cc]/10 text-[hsl(var(--muted-foreground))]'
                        }`}
                      >
                        {vessel.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${sourceBadgeClass(vessel.source)}`}
                      >
                        {vessel.source || 'manual'}
                      </span>
                    </td>
                    {(canEdit || canDelete) && (
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        {canEdit && (
                          <button
                            onClick={() => handleAisEnrich(vessel)}
                            disabled={enrichingId === vessel._id || !vessel.mmsi}
                            className="p-2 rounded-lg hover:bg-sky-50 text-sky-500 disabled:opacity-30 disabled:cursor-not-allowed"
                            title={vessel.mmsi ? 'Enrich from live AIS data' : 'Add MMSI to enable AIS lookup'}
                          >
                            <MdSatelliteAlt className={`w-4 h-4 ${enrichingId === vessel._id ? 'animate-spin' : ''}`} />
                          </button>
                        )}
                        {canEdit && (
                          <button
                            onClick={() => openEditModal(vessel)}
                            className="p-2 rounded-lg hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"
                            title="Edit"
                          >
                            <MdEdit className="w-4 h-4" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(vessel)}
                            className="p-2 rounded-lg hover:bg-red-50 text-red-500"
                            title="Remove"
                          >
                            <MdDelete className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="mt-6">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={total}
            itemsPerPage={limit}
          />
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
          <div className="w-full max-w-lg rounded-xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[hsl(var(--border))]">
              <h2 className="text-lg font-semibold">
                {editingVessel ? 'Edit Vessel' : 'Add Vessel'}
              </h2>
              <button onClick={closeModal} className="p-1 rounded hover:bg-[hsl(var(--muted))]">
                <MdClose className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                All fields are optional, but at least one identifier is required.
              </p>

              <div>
                <label className="block text-sm font-medium mb-1">Vessel Name</label>
                <input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-[hsl(var(--border))] rounded-lg"
                  placeholder="e.g. MV Example Star"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">IMO Number</label>
                  <input
                    value={formData.imoNumber}
                    onChange={(e) => setFormData({ ...formData, imoNumber: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-[hsl(var(--border))] rounded-lg font-mono"
                    placeholder="7-digit IMO"
                    inputMode="numeric"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">MMSI</label>
                  <input
                    value={formData.mmsi}
                    onChange={(e) => setFormData({ ...formData, mmsi: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-[hsl(var(--border))] rounded-lg font-mono"
                    placeholder="9-digit MMSI"
                    inputMode="numeric"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Call Sign</label>
                  <input
                    value={formData.callSign}
                    onChange={(e) => setFormData({ ...formData, callSign: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-[hsl(var(--border))] rounded-lg"
                    placeholder="Call sign"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Flag State</label>
                  <input
                    value={formData.flag}
                    onChange={(e) => setFormData({ ...formData, flag: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-[hsl(var(--border))] rounded-lg"
                    placeholder="e.g. Liberia"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Type of Vessel</label>
                <input
                  value={formData.vesselType}
                  onChange={(e) => setFormData({ ...formData, vesselType: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-[hsl(var(--border))] rounded-lg"
                  placeholder="e.g. Cargo, Tanker"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value as VesselStatus })
                  }
                  className="w-full px-3 py-2 text-sm border border-[hsl(var(--border))] rounded-lg"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-[hsl(var(--border))]">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm font-medium border border-[hsl(var(--border))] rounded-lg hover:bg-[hsl(var(--muted))]"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium bg-[#0088cc] text-white rounded-lg hover:bg-[#0077b3] disabled:opacity-60"
              >
                {isSubmitting ? 'Saving...' : editingVessel ? 'Update' : 'Add Vessel'}
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
